import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TranslationNamespace } from 'i18n';
import { FormFieldProps } from 'types';

import { TextField } from './TextField';
import { getTCommon, isValidString, useTFeature } from '../fields.utils';

const IBAN_REGEX = /^[A-Z]{2}[A-Z0-9]{4,30}$/;
const IBAN_MAX_LENGTH = 32;

export const Field: React.FC<FormFieldProps> = ({
  field: { name, value, pattern, patternExample, maxLength },
  onChange,
}) => {
  const { t } = useTFeature();
  const tCommon = getTCommon();
  const { t: tErrors } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'errors',
  });

  const [localValue, setLocalValue] = useState(value);
  const [touched, setTouched] = useState(false);

  const ibanRegex = useMemo(
    () => (pattern ? new RegExp(pattern) : IBAN_REGEX),
    [pattern],
  );

  const ibanMaxLength = useMemo(
    () => maxLength || IBAN_MAX_LENGTH,
    [maxLength],
  );

  const handleChange = useCallback(
    (value: string) => {
      if (!onChange || value?.length > ibanMaxLength) {
        return;
      }

      setLocalValue(value);
      onChange(isValidString(value, ibanRegex) ? value : '');
    },
    [ibanMaxLength, ibanRegex, onChange],
  );

  const error = useMemo(
    () =>
      touched && localValue && !isValidString(localValue, ibanRegex)
        ? tErrors('invalid')
        : undefined,
    [touched, localValue, ibanRegex, tErrors],
  );

  return (
    <TextField
      name={name}
      value={localValue}
      onChange={handleChange}
      onBlur={() => setTouched(true)}
      placeholder={t('fields.iban.placeholder')}
      hint={`${tCommon('common.example')}: ${patternExample}`}
      error={error}
    />
  );
};
